import {
  Mail,
  AtSign,
  Building2,
  GraduationCap,
  ShieldCheck,
  Lock,
  Eye,
  EyeOff,
  User,
  KeyRound,
  BadgeCheck,
} from "lucide-react";
import AdminLayout from "../layouts/AdminLayout";
import UniversityAdminLayout from "../layouts/UniversityAdminLayout";
import CompanyAdminLayout from "../layouts/CompanyAdminLayout";

function DynamicLayout({ roleKey, children }) {
  if (roleKey === "university_admin") return <UniversityAdminLayout>{children}</UniversityAdminLayout>;
  if (roleKey === "company_admin") return <CompanyAdminLayout>{children}</CompanyAdminLayout>;
  return <AdminLayout>{children}</AdminLayout>;
}

import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ToastContext";
import { changePassword } from "../api/services/profileService";

// ── constants ──

const ROLE_CONFIG = {
  company_admin: {
    label: "Company Admin",
    icon: Building2,
    color: "sky",
    badgeBg: "bg-sky-500/10",
    badgeText: "text-sky-400",
    badgeBorder: "border-sky-500/20",
    orgLabel: "Company Name",
  },
  university_admin: {
    label: "University Admin",
    icon: GraduationCap,
    color: "violet",
    badgeBg: "bg-violet-500/10",
    badgeText: "text-violet-400",
    badgeBorder: "border-violet-500/20",
    orgLabel: "University Name",
  },
  platform_admin: {
    label: "Platform Admin",
    icon: ShieldCheck,
    color: "amber",
    badgeBg: "bg-amber-500/10",
    badgeText: "text-amber-400",
    badgeBorder: "border-amber-500/20",
    orgLabel: null,
  },
};

const readonlyClass =
  "w-full rounded-lg border border-(--color-border) bg-(--color-bg-card) px-4 py-2.5 text-sm text-(--color-text-primary) placeholder:text-(--color-text-muted) cursor-default select-none opacity-80 focus:outline-none";

const inputClass =
  "w-full rounded-lg border border-(--color-border) bg-(--color-bg-card) px-4 py-2.5 text-sm text-(--color-text-primary) placeholder:text-(--color-text-muted) focus:outline-none focus:border-(--color-primary) focus:shadow-[0_0_0_3px_var(--color-primary-100)] transition-all";

const saveBtn =
  "px-5 py-2.5 rounded-lg text-sm font-medium bg-(--color-primary) text-(--color-text-on-primary) shadow-[0_0_10px_var(--color-shadow-primary)] hover:shadow-[0_0_16px_var(--color-shadow-primary-hover),0_0_32px_var(--color-shadow-primary)] transition-shadow duration-200 disabled:opacity-50 disabled:cursor-not-allowed";

// ── helpers ────────────────────────────────────────────────────────────────────

function getInitials(name) {
  return (name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function FormCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="rounded-xl dark:bg-(--color-bg-card) bg-(--color-bg)/30 border border-(--color-border)">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-(--color-border)">
        <div className="p-2 rounded-lg bg-(--color-bg)">
          <Icon size={15} className="text-(--color-primary)" />
        </div>
        <div>
          <p className="text-sm font-semibold text-(--color-text-primary)">{title}</p>
          {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
        </div>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function InfoPill({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-(--color-bg-card) border border-(--color-border)">
      <Icon size={14} className="text-slate-400 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-sm font-medium text-(--color-text-primary) truncate">{value || "—"}</p>
      </div>
    </div>
  );
}

// ── main ──

export default function AdminsProfilePage() {
  const { user } = useAuth();
  const { showToast } = useToast();

  const roleKey =
    user?.roles?.find((r) => Object.keys(ROLE_CONFIG).includes(r)) ?? "platform_admin";
  const role = ROLE_CONFIG[roleKey];
  const RoleIcon = role.icon;

  const personal = {
    fullName:
      user?.full_name ??
      [user?.given_name, user?.family_name].filter(Boolean).join(" ") ??
      user?.name ??
      "",
    email: user?.email ?? "",
    username: user?.preferred_username ?? user?.username ?? "",
  };

  const orgName = user?.organization_name ?? "";

  // ── password ──
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwErrors, setPwErrors] = useState({});
  const [pwLoading, setPwLoading] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });

  const handlePwChange = (e) => {
    const { name, value } = e.target;
    setPw((p) => ({ ...p, [name]: value }));
    if (pwErrors[name]) setPwErrors((p) => ({ ...p, [name]: "" }));
  };

  const savePassword = async () => {
    const errs = {};
    if (!pw.current) errs.current = "Required";
    if (!pw.next) errs.next = "Required";
    else if (pw.next.length < 8) errs.next = "Minimum 8 characters";
    if (!pw.confirm) errs.confirm = "Required";
    else if (pw.next !== pw.confirm) errs.confirm = "Passwords do not match";
    setPwErrors(errs);
    if (Object.keys(errs).length) return;

    setPwLoading(true);
    try {
      await changePassword(pw.current, pw.next, pw.confirm);
      setPw({ current: "", next: "", confirm: "" });
      showToast("Password updated successfully.", "success");
    } catch (err) {
      setPwErrors({ current: err?.message || "Failed to update password. Please try again." });
    } finally {
      setPwLoading(false);
    }
  };

  const renderPwField = (field, label) => (
    <Field label={label} error={pwErrors[field]}>
      <div className="relative">
        <input
          name={field}
          type={showPw[field] ? "text" : "password"}
          value={pw[field]}
          onChange={handlePwChange}
          placeholder="••••••••"
          className={`${inputClass} pr-10 ${pwErrors[field] ? "border-red-500" : ""}`}
        />
        <button
          type="button"
          onClick={() => setShowPw((p) => ({ ...p, [field]: !p[field] }))}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-(--color-text-primary) transition-colors"
        >
          {showPw[field] ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </Field>
  );

  const initials = getInitials(personal.fullName || personal.username);

  return (
    <DynamicLayout roleKey={roleKey}>
      <div className="min-h-screen px-8 py-8">

        {/* Page title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-(--color-text-primary)">Profile</h1>
          <p className="text-xs mt-0.5 text-slate-400">Manage your account details and security.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 items-start">

          {/* ── LEFT PANEL ──*/}
          <div className="space-y-4">

            {/* Identity card */}
            <div className="rounded-xl dark:bg-(--color-bg-card) bg-(--color-bg)/30 border border-(--color-border) overflow-hidden">
              <div className="h-20" />
              <div className="px-5 pb-5 -mt-9">
                <div className="w-16 h-16 rounded-2xl bg-(--color-primary) flex items-center justify-center ring-4 ring-(--color-bg-card) shadow-lg mb-3">
                  <span className="text-xl font-bold text-(--color-text-on-primary)">{initials}</span>
                </div>
                <p className="text-base font-bold text-(--color-text-primary) truncate">
                  {personal.fullName || personal.username || "—"}
                </p>
                <p className="text-xs text-slate-400 mb-3">@{personal.username || "—"}</p>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${role.badgeBg} ${role.badgeText} ${role.badgeBorder}`}>
                  <RoleIcon size={11} />
                  {role.label}
                </span>
              </div>
            </div>

            {/* Account info pills */}
            <div className="rounded-xl dark:bg-(--color-bg-card) bg-(--color-bg)/30 border border-(--color-border) p-4 space-y-2.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pb-1">Account Info</p>
              <InfoPill icon={Mail} label="Email" value={personal.email} />
              <InfoPill icon={AtSign} label="Username" value={`@${personal.username}`} />
              <InfoPill icon={RoleIcon} label="Role" value={role.label} />
              {roleKey !== "platform_admin" && (
                <InfoPill icon={Building2} label={role.orgLabel} value={orgName} />
              )}
            </div>

            {/* Security status */}
            <div className="rounded-xl dark:bg-(--color-bg-card) bg-(--color-bg)/30 border border-(--color-border) p-4 space-y-2.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 pb-1">Security</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BadgeCheck size={14} className="text-emerald-400" />
                  <span className="text-sm text-(--color-text-primary)">Email verified</span>
                </div>
                <span className="text-xs text-emerald-400 font-medium">Active</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <KeyRound size={14} className="text-slate-400" />
                  <span className="text-sm text-(--color-text-primary)">Password</span>
                </div>
                <span className="text-xs text-slate-400">••••••••</span>
              </div>
            </div>
          </div>

          {/* ── RIGHT PANEL ── */}
          <div className="space-y-5">

            {/* 1. Personal Information — read-only */}
            <FormCard icon={User} title="Personal Information" subtitle="Your account details">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full Name">
                  <input
                    readOnly
                    value={personal.fullName || "—"}
                    className={readonlyClass}
                  />
                </Field>
                <Field label="Email Address">
                  <input
                    readOnly
                    value={personal.email || "—"}
                    className={readonlyClass}
                  />
                </Field>
              </div>
              <Field label="Username">
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm select-none">@</span>
                  <input
                    readOnly
                    value={personal.username || "—"}
                    className={`${readonlyClass} pl-7`}
                  />
                </div>
              </Field>
            </FormCard>

            {/* 2. Change Password — interactive */}
            <FormCard icon={Lock} title="Change Password" subtitle="Keep your account secure with a strong password">
              {renderPwField("current", "Current Password")}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderPwField("next", "New Password")}
                {renderPwField("confirm", "Confirm New Password")}
              </div>
              <div className="flex justify-end pt-1">
                <button onClick={savePassword} disabled={pwLoading} className={saveBtn}>
                  {pwLoading ? "Updating…" : "Update Password"}
                </button>
              </div>
            </FormCard>

            {/* 3. Organization Information — read-only */}
            <FormCard
              icon={RoleIcon}
              title="Organization Information"
              subtitle={roleKey === "platform_admin" ? "Not affiliated with an organization" : `Your ${role.orgLabel?.toLowerCase()}`}
            >
              {roleKey === "platform_admin" ? (
                <div className="flex items-center gap-3 px-4 py-3.5 rounded-lg bg-amber-500/5 border border-amber-500/15">
                  <ShieldCheck size={15} className="text-amber-400 shrink-0" />
                  <p className="text-sm text-slate-400">
                    Platform admins have platform-wide access and are not tied to any organization.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={role.orgLabel}>
                    <input
                      readOnly
                      value={orgName || "—"}
                      className={readonlyClass}
                    />
                  </Field>
                  <Field label="Organization Type">
                    <input
                      readOnly
                      value={roleKey === "university_admin" ? "University" : "Company"}
                      className={readonlyClass}
                    />
                  </Field>
                </div>
              )}
            </FormCard>

          </div>
        </div>
      </div>
    </DynamicLayout>
  );
}